import type { TablesInsert } from '@/integrations/supabase/types';
import { determineStandardAssetClass } from '@/utils/asset-class';
import { schedulePortfolioStatusRecompute } from '@/utils/portfolioStatusUpdater';
import { logger } from '@/services/LoggingService';
import type { NormalizedAsset } from '@/types/assets';
import { getPortfolioType } from '@/services/portfolio/PortfolioService';
import { getDefaultCurrency } from '@/services/profile/UserProfileService';
import { insertAllocation } from '@/services/portfolio/PortfolioAllocationsService';
import { useAuthStore } from '@/stores/authStore';

export interface PerformAllocationInsertParams {
  portfolioId: string;
  selectedAsset: NormalizedAsset;
}

async function resolvePortfolioType(portfolioId: string): Promise<string> {
  const t = await getPortfolioType(portfolioId);
  return t || 'personal';
}

async function resolveCurrencyCode(portfolioId: string): Promise<string> {
  // Default currency
  const currencyCode = 'CAD';
  try {
    const lsKey = `portfolio:currency:${portfolioId}`;
    const fromLs = typeof window !== 'undefined' ? window.localStorage.getItem(lsKey) : null;
    if (fromLs && fromLs.trim()) {
      return fromLs.trim().toUpperCase();
    }

    const uid = useAuthStore.getState().user?.id;
    const dc = uid ? await getDefaultCurrency(uid) : null;
    if (dc) return String(dc).toUpperCase();
  } catch {
    // best-effort; fallback to default
  }
  return currencyCode;
}

function computeNormalizedAssetType(selectedAsset: NormalizedAsset): string {
  switch (selectedAsset.source) {
    case 'stock_fundamentals':
      return determineStandardAssetClass(undefined, 'stock_fundamental');
    case 'funds_raw':
      return determineStandardAssetClass((selectedAsset as any).assetClass, 'fund_raw');
    case 'funds':
      return determineStandardAssetClass((selectedAsset as any).assetClass, 'fund');
    default:
      return 'unclassified';
  }
}

export async function performAllocationInsert({ portfolioId, selectedAsset }: PerformAllocationInsertParams) {
  // Determine asset type
  const assetType = computeNormalizedAssetType(selectedAsset);
  logger.debug('allocation-insert', 'Computed normalized asset type', { assetType, source: selectedAsset.source });

  // Resolve portfolio type and currency
  const [pType, currencyCode] = await Promise.all([
    resolvePortfolioType(portfolioId),
    resolveCurrencyCode(portfolioId),
  ]);

  // Build insert payload
  const insertPayload: TablesInsert<'portfolio_allocations'> = {
    portfolio_id: portfolioId,
    asset_id: selectedAsset.id,
    asset_type: assetType,
    allocation_percentage: 0,
    currency_code: currencyCode,
  };
  if (pType !== 'model') {
    insertPayload.allocation_value = 0;
    insertPayload.is_value_locked = true;
  }

  await insertAllocation(insertPayload);

  // Debounced update of portfolio.status based on new allocations
  try { schedulePortfolioStatusRecompute(portfolioId); } catch {}

  return insertPayload;
}
